import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import motion from "@/lib/lazy-motion-tags";
import { LazyAnimatePresence as AnimatePresence } from "@/lib/LazyMotion";
import logoJpg from "@/assets/logo.png";
import logoWebp from "@/assets/logo.png.webp";
import logoAvif from "@/assets/logo.png.avif";
import ResponsiveImage from "@/components/ResponsiveImage";
import { trackPhoneCall } from "@/lib/analytics";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { to: "/", label: "الرئيسية" },
    { to: "/about", label: "من نحن" },
    { to: "/why-wpc", label: "لماذا WPC" },
    { to: "/products", label: "المنتجات" },
    { to: "/services", label: "الخدمات" },
    { to: "/projects", label: "المشاريع" },
    { to: "/contact", label: "تواصل معنا" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-effect shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <ResponsiveImage src={logoJpg} webp={logoWebp} avif={logoAvif} alt="New Business" className="h-12 w-auto" width={48} height={48} />
            <div className="hidden md:flex flex-col">
              <span className="text-xl font-bold text-foreground">New Business</span>
              <span className="text-xs text-muted-foreground">أبواب WPC المتكامل</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === link.to ? "text-primary" : "text-foreground"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <Button asChild className="bg-gradient-primary text-white font-bold shadow-elegant hover:shadow-glow transition-all duration-300 hover:-translate-y-1">
              <a href="tel:+966552351116" onClick={() => trackPhoneCall("navbar_desktop")}>
                <Phone className="ml-2 h-4 w-4" />
                اتصل بنا
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "إغلاق القائمة" : "فتح القائمة"}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass-effect"
          >
            <div className="container mx-auto px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-2 text-base font-medium transition-colors hover:text-primary ${
                    location.pathname === link.to ? "text-primary" : "text-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Button asChild className="w-full bg-gradient-primary text-white font-bold transition-transform duration-300 hover:-translate-y-1">
                <a
                  href="tel:+966552351116"
                  onClick={() => {
                    trackPhoneCall("navbar_mobile");
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <Phone className="ml-2 h-4 w-4" />
                  اتصل بنا
                </a>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
